<script setup lang="ts">
  import { computed, onMounted, onUnmounted } from 'vue';
  import { ColoredButton, IconImage, SearchNoResults, SimpleLoading, TableHeader, Tooltip } from '@/shared/ui';
  import { useFilterAndSort, useTableState } from '@/shared/lib';
  import orderIcon from '@/shared/assets/icons/Order.svg';
  import orderWhite from '@/shared/assets/icons/OrderWhite.svg';
  import PlusButtonIcon from '@/shared/assets/icons/PlusButtonIcon.svg';
  import type { SettingsListItem } from '../model/settings.types';
  import SettingsSectionEmptyState from './SettingsSectionEmptyState.vue';
  import SettingsSectionRow from './SettingsSectionRow.vue';

  const props = withDefaults(
    defineProps<{
      title: string;
      columnLabel: string;
      emptyMessage: string;
      items: readonly SettingsListItem[];
      orderOptions: Array<{ label: string; sort: string; order: string }>;
      isLoading?: boolean;
      isSearchActive?: boolean;
      /** The listing is unknown, not empty. */
      error?: string | null;
      errorMessage?: string;
    }>(),
    {
      isLoading: false,
      isSearchActive: false,
      error: null,
      errorMessage: 'Ops! Não conseguimos carregar esta lista agora. Tente novamente em instantes.',
    }
  );

  const emit = defineEmits<{
    add: [];
    edit: [id: number];
    delete: [id: number];
    'update:sort': [sort: string, order: string];
    'filter-clicked': [];
  }>();

  const {
    isOrderSelected,
    isOrderDropdownOpen,
    selectedOption,
    dropdownPosition,
    orderButtonRef,
    handleOrder,
    sortOptions,
    handleClickOutside,
  } = useFilterAndSort(emit);

  const { shouldShowError, shouldShowEmpty } = useTableState({
    error: () => props.error,
    data: () => props.items,
    loading: () => props.isLoading,
  });

  const hasScrollableRows = computed(
    () => !props.isLoading && !props.error && props.items.length > 0
  );
  const showEmptyMessage = computed(() => shouldShowEmpty.value && !props.isSearchActive);
  const showNoResults = computed(() => shouldShowEmpty.value && props.isSearchActive);
  const showRows = computed(() => !props.isLoading && !props.error && props.items.length > 0);

  onMounted(() => document.addEventListener('click', handleClickOutside));
  onUnmounted(() => document.removeEventListener('click', handleClickOutside));
</script>

<template>
  <section class="bg-white-01 border border-gray-02 rounded-2xl">
    <header class="flex items-center justify-between px-6 py-4 border-b border-gray-02">
      <h3 class="text-sm font-semibold text-gray-07">
        {{ title }}
      </h3>

      <div class="flex items-center gap-4 relative">
        <div
          v-if="orderOptions.length > 0"
          ref="orderButtonRef"
          class="relative self-center"
        >
          <ColoredButton
            text="Ordenar por"
            :icon="isOrderSelected ? orderWhite : orderIcon"
            icon-alt="Ordenar por"
            :outlined="!isOrderSelected"
            class="!h-[42px]"
            @click="handleOrder"
          />

          <Teleport to="body">
            <div
              v-if="isOrderDropdownOpen"
              class="fixed w-56 p-2 bg-white-01 rounded-2xl shadow-2xl z-50"
              :style="{ top: dropdownPosition.top + 'px', right: dropdownPosition.right + 'px' }"
            >
              <button
                v-for="option in orderOptions"
                :key="option.label"
                type="button"
                class="block w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-gray-07"
                :class="{
                  'bg-gray-02':
                    selectedOption?.sort === option.sort && selectedOption?.order === option.order,
                }"
                @click="sortOptions(option.sort, option.order)"
              >
                {{ option.label }}
              </button>
            </div>
          </Teleport>
        </div>

        <Tooltip
          content="Adicionar"
          placement="top"
          class="flex h-[42px] w-[42px] items-center justify-center self-center"
        >
          <button
            type="button"
            aria-label="Adicionar"
            class="flex size-5 shrink-0 items-center justify-center text-[var(--theme-secondary-ink)]"
            @click="emit('add')"
          >
            <IconImage
              :src="PlusButtonIcon"
              alt="Adicionar"
            />
          </button>
        </Tooltip>
      </div>
    </header>

    <div
      data-testid="settings-section-scroll"
      :class="hasScrollableRows ? 'max-h-[300px] overflow-y-auto' : ''"
    >
      <table class="min-w-full">
        <thead v-if="!showNoResults">
          <tr>
            <TableHeader
              :text="columnLabel"
              th-class="sticky top-0 z-10 bg-white-01 border-b border-gray-02 text-left text-xs font-semibold text-gray-07 pl-6 py-3 align-middle"
            />
            <TableHeader
              text="Ações"
              th-class="sticky top-0 z-10 bg-white-01 border-b border-gray-02 text-right text-xs font-semibold text-gray-07 pr-6 py-3 w-26 align-middle"
            />
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td
              colspan="2"
              data-testid="settings-section-loading"
            >
              <div class="flex min-h-40 items-center justify-center">
                <SimpleLoading />
              </div>
            </td>
          </tr>
          <tr v-else-if="shouldShowError">
            <td
              colspan="2"
              data-testid="settings-section-error"
              class="min-h-40 px-6 py-10 text-center text-sm text-red-16"
            >
              {{ errorMessage }}
            </td>
          </tr>
          <tr v-else-if="showNoResults">
            <td colspan="2">
              <SearchNoResults />
            </td>
          </tr>
          <template v-else-if="showRows">
            <SettingsSectionRow
              v-for="item in items"
              :id="item.id"
              :key="item.id"
              :name="item.name"
              @edit="emit('edit', $event)"
              @delete="emit('delete', $event)"
            />
          </template>
        </tbody>
      </table>

      <SettingsSectionEmptyState
        v-if="showEmptyMessage"
        :message="emptyMessage"
      />
    </div>
  </section>
</template>
